// Copy for the FieldInfo hover-then-details buttons on the coded status fields in Subscription
// Details (Zuora). Descriptions are drafted from the code paths that derive each field -
// helpers/product-status.ts, helpers/raw-subscription-doc.ts, and the Tier/SubscriptionState
// enums - not from an approved style guide. Business/Billing should confirm the wording before
// it's treated as authoritative copy.
export interface FieldHelpValue {
  label: string;
  description: string;
}

export interface FieldHelpContent {
  title: string;
  description: string;
  values: FieldHelpValue[];
  extraNote?: string;
}

export type FieldHelpKey =
  | "tier"
  | "productStatus"
  | "subscriptionStatus"
  | "zuoraSubscriptionStatus"
  | "paymentStatus";

export const FIELD_HELP: Record<FieldHelpKey, FieldHelpContent> = {
  tier: {
    title: "Tier",
    description:
      "The plan level this organisation sits on. Normally read from the subscription record, but an activated contract flagged as Enterprise overrides it.",
    values: [
      {
        label: "Free",
        description: "No paid subscription. Free plan limits apply.",
      },
      {
        label: "Premium",
        description: "Paid self-serve or sales-assisted plan.",
      },
      {
        label: "Enterprise",
        description:
          "Overrides whatever tier the subscription record holds, whenever the org has any contract with status = Activated and hasEnterprise = true.",
      },
      {
        label: "Unspecified",
        description:
          "No tier set on the record - usually a data issue worth raising.",
      },
    ],
    extraNote:
      "Enterprise tier is calculated based on whether the org has any contract with status = Activated and hasEnterprise = true.",
  },
  productStatus: {
    title: "Product Status",
    description:
      "This determines whether the users of the organization can use the product.",
    values: [
      {
        label: "Active",
        description:
          "Organization can use product features within their allocation subscription tier.",
      },
      {
        label: "Active (Trialing)",
        description: "Organization is trialing premium features.",
      },
      {
        label: "Suspended",
        description:
          "Organization is blocked from product usage, i.e. can not log into the product. Suspension is triggered via Periscope and can occur for various reasons such as extended overdue payments.",
      },
      {
        label: "Inactive",
        description:
          "Organization can still access the product but may need to reactivate their subscription. Inactive usually means the subscription is either canceled or their trial has expired.",
      },
      {
        label: "N/A",
        description:
          "Could not derive product status. Either the organization or contract data failed to load.",
      },
    ],
  },
  subscriptionStatus: {
    title: "Subscription Status",
    description: "The state of the subscription record itself.",
    values: [
      { label: "Active", description: "Live, billing on schedule." },
      {
        label: "Trialing",
        description: "In a trial. Converts or expires at the trial end date.",
      },
      {
        label: "Past Due",
        description:
          "An invoice is unpaid past its due date. Still active - see Payment Status for how far collection has escalated.",
      },
      {
        label: "Canceled",
        description:
          "Either the org chose not to renew, or the subscription was auto-canceled due to missed payments. Access may continue until the end of the paid period.",
      },
      {
        label: "Unspecified",
        description: "No state on the record. Treat as a data issue.",
      },
    ],
    extraNote:
      'A "(Decoupled)" suffix means the subscription has no Zuora subscription ID yet - it exists in SafetyCulture but not (or not yet) in Zuora.',
  },
  zuoraSubscriptionStatus: {
    title: "Zuora Subscription Status",
    description:
      "The subscription status as recorded directly on the Zuora subscription (Subscription.SubscriptionStatus__c), read from the raw Zuora document. This can differ from the Subscription Status above, which reflects SafetyCulture's own subscription record state.",
    values: [
      { label: "Active", description: "Live, billing on schedule." },
      {
        label: "Active (Out of Term)",
        description:
          "Still active, but past the end of its contracted term - typically rolling month-to-month until renewed or cancelled.",
      },
      {
        label: "Active (Pending Cancellation)",
        description:
          "Still active, but a cancellation has been scheduled. Shown with the cancellation date from Subscription.SubscriptionStatusDate__c.",
      },
      {
        label: "Cancelled",
        description: "The subscription has ended in Zuora.",
      },
      {
        label: "Scheduled to Start",
        description:
          "Not yet active - the subscription is scheduled to begin. Shown with the start date from Subscription.SubscriptionStatusDate__c.",
      },
      {
        label: "Unknown",
        description:
          "No subscription status on the raw Zuora document. Check the account directly in Zuora.",
      },
    ],
    extraNote:
      'Subscription.SubscriptionStatusDate__c is only appended for "Scheduled to Start" (as the start date) and "Active (Pending Cancellation)" (as the cancellation date) - it\'s not read for any other status.',
  },
  paymentStatus: {
    title: "Payment Status",
    description: "How the org is tracking against its invoices.",
    values: [
      { label: "Paid", description: "No outstanding balance." },
      {
        label: "Unpaid (Within Terms)",
        description: "An invoice is open but not yet due. No action needed.",
      },
      {
        label: "Unpaid (Overdue - Grace Period)",
        description:
          "An item on the account is overdue, within 14 days of its due date.",
      },
      {
        label: "Unpaid (Overdue - Dunning)",
        description:
          "An item on the account is overdue, more than 14 days past its due date.",
      },
      {
        label: "Unknown",
        description:
          "No payment status on the document. Check the account directly in Zuora.",
      },
    ],
    extraNote:
      "Any value not listed here falls back to a grey pill - if you see one, it came straight from Zuora and is worth reporting.",
  },
};
